import { useState, type ReactNode } from 'react';

/**
 * Endpoint PUBLIC (sans auth) qui retourne le logo de l'organisation si
 * configuré, 404 sinon. Défini dans `src/server/modules/config/config.router.ts`.
 * Défense en profondeur : le serveur restreint le content-type à PNG/JPEG ;
 * SVG est refusé à l'upload. Voir SECURITY.md §organization.public-logo.
 */
const PUBLIC_LOGO_URL = '/api/v1/config/public/logo';

/**
 * Conteneur des pages d'authentification : login, vérification 2FA, setup 2FA.
 * Plein écran sur `bg-page`, card centrée blanche avec shadow-sm.
 *
 * En-tête branding : tente de charger le logo organisation via l'endpoint
 * public `PUBLIC_LOGO_URL`. En cas d'erreur (404 = aucun logo configuré, ou
 * réseau indisponible), fallback transparent sur le texte historique.
 */
export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  // L'image elle-même gère le chargement ; `logoFailed` passe à true si le
  // navigateur émet `onerror` (404, network, CORS) → on bascule sur le texte.
  const [logoFailed, setLogoFailed] = useState(false);

  return (
    <main className="flex min-h-[calc(100vh/var(--app-zoom))] flex-col items-center justify-center bg-bg-page px-4 py-12">
      <div className="mb-8 text-center">
        {logoFailed ? (
          <>
            <p className="text-sm font-semibold tracking-tight text-brand">
              CRM Courtage Assurance
            </p>
            <p className="text-xs text-text-muted">Adélie Assurances - espace interne</p>
          </>
        ) : (
          <img
            src={PUBLIC_LOGO_URL}
            alt="Logo"
            // Bornes raisonnables : largeur max 280px (proportionnel à la
            // card de login 420px), hauteur 64px pour aérer. Object-contain
            // préserve le ratio pour tout format d'image.
            className="mx-auto h-16 max-w-[280px] object-contain"
            onError={() => setLogoFailed(true)}
          />
        )}
      </div>
      <div className="w-full max-w-[420px] rounded-lg border border-border-subtle bg-white p-8 shadow-sm">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold tracking-tight text-brand">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-text-muted">{subtitle}</p>}
        </div>
        {children}
      </div>
      <p className="mt-6 text-[11px] text-text-muted">© 2026 CRM interne · v0.1</p>
    </main>
  );
}

export function AuthSeparator({ label = 'ou' }: { label?: string }) {
  return (
    <div className="relative my-6">
      <div className="absolute inset-0 flex items-center" aria-hidden>
        <div className="w-full border-t border-border-subtle" />
      </div>
      <div className="relative flex justify-center text-xs">
        <span className="bg-white px-2 text-text-muted">{label}</span>
      </div>
    </div>
  );
}
